<template>
  <div class="devices-tab">
    <div class="devices-section">
      <!-- Section Header -->
      <div class="section-header">
        <h3 class="section-title">
          {{ devicesAny ? $t("globals.devices") + " (" + devicesLength + ")" : $t("globals.devices") }}
        </h3>
        <div class="header-actions">
          <!-- Export CSV -->
          <button
            v-if="devicesAny"
            class="action-btn export-btn"
            @click="downloadDevicesCSV"
            :title="$t('globals.download_csv')"
          >
            <v-icon name="file-download" :spin="downloadCsvLoading"></v-icon>
            {{ $t('globals.export') }}
          </button>

          <!-- Add Devices Dropdown -->
          <div class="dropdown-wrapper" v-if="status != 'terminated' && status != 'canceled'" ref="addDropdown">
            <button
              class="primary-action-btn"
              @click="toggleAddMenu"
              :title="$t('globals.add_devices_from_stock')"
            >
              <v-icon name="plus-circle"></v-icon>
              {{ $t('globals.add') }}
            </button>
            <div v-if="showAddMenu" class="dropdown-menu">
              <button @click="openAddCsvModal" class="dropdown-item">
                .csv
              </button>
              <button @click="openAddManualModal" class="dropdown-item">
                {{ $t("globals.manual") }}
              </button>
            </div>
          </div>

          <!-- Actions for Selected -->
          <div class="dropdown-wrapper" v-if="devicesAny" ref="actionsDropdown">
            <div v-b-tooltip.hover :title="hasPendingTerminationDevice ? $t('globals.blocked_devices_for_pending_termination') : ''">
              <button
                class="primary-action-btn"
                @click="toggleActionsMenu"
                :disabled="!selectedItemsAny || hasPendingTerminationDevice"
                :class="{ 'disabled': !selectedItemsAny }"
                
              >
                {{ $t('globals.actions_selected') }}
              </button>
            </div>
            <div v-if="showActionsMenu && !hasPendingTerminationDevice" class="dropdown-menu">
              <button @click="handleAction('terminate')" class="dropdown-item danger">
                {{ $t('globals.terminate') }}
              </button>
              <button @click="handleAction('stock')" class="dropdown-item">
                {{ $t('globals.stock') }}
              </button>
              <button @click="handleAction('warranty')" class="dropdown-item">
                {{ $t('globals.warranty') }}
              </button>
              <button @click="openTransferModal" class="dropdown-item">
                {{ $t('globals.transfer') }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Filters Section -->
      <div class="filters-section">
        <div class="search-container">
          <input
            type="text"
            v-model="filter"
            class="search-input"
            :placeholder="$t('globals.type_to_search')"
          />
          <button
            v-if="filter"
            class="clear-btn"
            @click="filter = ''"
          >
            {{ $t("globals.clear") }}
          </button>
        </div>
        <div class="filter-item">
          <label class="filter-label">{{ $t("globals.per_page") }}</label>
          <select v-model="perPage" class="filter-select">
            <option :value="10">10</option>
            <option :value="20">20</option>
            <option :value="30">30</option>
          </select>
        </div>
      </div>

      <!-- Selected Devices Info -->
      <div v-if="devicesAny" class="selected-info">
        {{ $t("globals.selected_devices") }}: <strong>{{ noSelectedDevices }}</strong>
      </div>

      <!-- Table -->
      <div class="table-wrapper">
        <table class="modern-table devices-table">
          <thead>
            <tr>
              <th class="checkbox-col">
                <input
                  type="checkbox"
                  @change="toggleFiltered($event.target.checked)"
                  :disabled="!devicesAny"
                  class="table-checkbox"
                />
              </th>
              <th @click="sortTable('id')" class="sortable">
                ID
                <span class="sort-icon" v-if="sortBy === 'id'">
                  {{ sortDesc ? '▼' : '▲' }}
                </span>
              </th>
              <th @click="sortTable('imei')" class="sortable">
                IMEI
                <span class="sort-icon" v-if="sortBy === 'imei'">
                  {{ sortDesc ? '▼' : '▲' }}
                </span>
              </th>
              <th @click="sortTable('product')" class="sortable">
                {{ $t("globals.product") }}
                <span class="sort-icon" v-if="sortBy === 'product'">
                  {{ sortDesc ? '▼' : '▲' }}
                </span>
              </th>
              <th>{{ $t("globals.batch") }}</th>
              <th @click="sortTable('status')" class="sortable">
                {{ $t("globals.status") }}
                <span class="sort-icon" v-if="sortBy === 'status'">
                  {{ sortDesc ? '▼' : '▲' }}
                </span>
              </th>
              <th @click="sortTable('terminate_at')" class="sortable">
                {{ $t("globals.scheduled_termination_at") }}
                <span class="sort-icon" v-if="sortBy === 'terminate_at'">
                  {{ sortDesc ? '▼' : '▲' }}
                </span>
              </th>
              <th @click="sortTable('return_process')" class="sortable">
                {{ $t("globals.return_process") }}
                <span class="sort-icon" v-if="sortBy === 'return_process'">
                  {{ sortDesc ? '▼' : '▲' }}
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="device in paginatedDevices" :key="device.id">
              <td class="checkbox-col">
                <input
                  type="checkbox"
                  v-model="device.selected"
                  class="table-checkbox"
                />
              </td>
              <td>{{ device.id }}</td>
              <td>
                <router-link
                  class="customer-link"
                  :to="{name: 'device', params:{ id: device.id }}"
                >
                  {{ device.imei }}
                </router-link>
              </td>
              <td>{{ device.product }}</td>
              <td>{{ device.batch }}</td>
              <td>
                <span class="status-badge" :class="'status-' + (device.status || '').toLowerCase()">
                  {{ device.status }}
                </span>
              </td>
              <td>
               
                <div class="terminate-cell">
                  <span v-if="device.pending_termination && device.pending_termination.terminate_at">
                    {{ formatTerminationDate(device.pending_termination.terminate_at) }}
                  </span>
                  <span v-if="device.pending_termination && !device.pending_termination.terminate_at">
                    {{ $t("globals.awaiting_penalty_payment") }} 
                  </span>
                  <button 
                    v-if="device.pending_termination"
                    class="terminate-btn"
                    @click="undoTermination(device.pending_termination.id)"
                  >
                    {{ $t("globals.undo") }}
                  </button>
                </div>
              </td>
              <td>{{ device.pending_termination ? device.pending_termination.return_process : '-' }}</td>
            </tr>
            <tr v-if="!paginatedDevices || paginatedDevices.length === 0">
              <td colspan="8" class="empty-state-cell">{{ $t("globals.no_devices_found") }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div v-if="filteredDevices.length > 0" class="pagination-section">
        <div class="controls-left">
          <span class="summary-label">
            {{ $t("globals.showing") }} {{ ((currentPage - 1) * perPage) + 1 }}-{{ Math.min(currentPage * perPage, filteredDevices.length) }}
          </span>
        </div>
        <div class="pagination-controls">
          <button
            class="page-btn"
            @click="currentPage--"
            :disabled="currentPage === 1"
          >
            &lt;
          </button>
          <button
            class="page-btn"
            @click="currentPage++"
            :disabled="currentPage === totalPages"
          >
            &gt;
          </button>
        </div>
      </div>
    </div>
    <!-- Modals -->
    <ulu-add-devices-csv-modal
      :show="addDevicesCsvModal.show"
      :close="closeModals"
    />
    <ulu-add-devices-manualy-modal
      :show="addDevicesManualyModal.show"
      :close="closeModals"
    />
    <ulu-selected-devices-transfer-modal
      v-if="devicesAny"
      :show="selectedDevicesTransferModal.show"
      :devices="devices.filter(d => d.selected)"
      :close="closeModals"
    />
    <!-- Hidden download element -->
    <a
      href=""
      id="csvdownloadlink"
      ref="csvfiledownload"
      hidden="true"
      download=""
    ></a>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from "vuex";
import { format } from "date-fns";

export default {
  props: {
    refreshOrder: Function,
    devices: Array,
    status: String
  },
  components: {
    "ulu-add-devices-csv-modal": () =>
      import("@/components/orders/order/order_devices_tab/AddDevicesCsvModal.vue"),
    "ulu-add-devices-manualy-modal": () =>
      import("@/components/orders/order/order_devices_tab/AddDevicesManualyModal.vue"),
    "ulu-selected-devices-transfer-modal": () =>
      import("@/components/orders/order/order_devices_tab/TransferDevicesModal.vue")
  },
  data() {
    return {
      selected: false,
      downloadCsvLoading: false,
      showAddMenu: false,
      showActionsMenu: false,
      ignoreNextClickOutside: false,
      selectedDevicesTransferModal: {
        show: false
      },
      addDevicesCsvModal: {
        show: false
      },
      addDevicesManualyModal: {
        show: false
      },
      sortBy: "status",
      sortDesc: false,
      filter: "",
      currentPage: 1,
      perPage: 10
    };
  },
  methods: {
    ...mapActions("axios_mod", ["simpleMutation", "simpleFetch"]),
    ...mapMutations(["setShowLoadingModal"]),
    formatTerminationDate(date) {
      if (!date) return '-';
      try {
        return format(new Date(date), 'dd/MM/yyyy');
      } catch (e) {
        return date;
      }
    },
    handleClickOutside(event) {
      // Ignore if we just toggled a dropdown
      if (this.ignoreNextClickOutside) {
        this.ignoreNextClickOutside = false;
        return;
      }

      // Close add menu if clicking outside
      if (this.$refs.addDropdown && !this.$refs.addDropdown.contains(event.target)) {
        this.showAddMenu = false;
      }
      // Close actions menu if clicking outside
      if (this.$refs.actionsDropdown && !this.$refs.actionsDropdown.contains(event.target)) {
        this.showActionsMenu = false;
      }
    },
    toggleAddMenu(event) {
      event.stopPropagation();
      this.ignoreNextClickOutside = true;
      this.showAddMenu = !this.showAddMenu;
      this.showActionsMenu = false;
    },
    toggleActionsMenu(event) {
      event.stopPropagation();
      if (!this.selectedItemsAny) return;
      this.ignoreNextClickOutside = true;
      this.showActionsMenu = !this.showActionsMenu;
      this.showAddMenu = false;
    },
    closeAddMenu() {
      this.showAddMenu = false;
    },
    closeActionsMenu() {
      this.showActionsMenu = false;
    },
    openAddCsvModal() {
      this.addDevicesCsvModal.show = true;
      this.showAddMenu = false;
    },
    openAddManualModal() {
      this.addDevicesManualyModal.show = true;
      this.showAddMenu = false;
    },
    handleAction(action) {
      this.devicesAction(action);
      this.showActionsMenu = false;
    },
    openTransferModal() {
      this.selectedDevicesTransferModal.show = true;
      this.showActionsMenu = false;
    },
    sortTable(field) {
      if (this.sortBy === field) {
        this.sortDesc = !this.sortDesc;
      } else {
        this.sortBy = field;
        this.sortDesc = false;
      }
    },
    toggleAll(selected) {
      if (selected) {
        this.selectAll();
      } else {
        this.deselectAll();
      }
    },
    toggleFiltered(checked) {
      this.filteredDevices.forEach(e => e.selected = checked);
    },
    selectAll() {
      this.devices.forEach(e => e.selected = true);
    },
    deselectAll() {
      this.devices.forEach(e => e.selected = false);
    },
    closeModals() {
      this.addDevicesCsvModal.show = false;
      this.addDevicesManualyModal.show = false;
      this.selectedDevicesTransferModal.show = false;

      this.refreshOrder();
    },
    downloadDevicesCSV() {
      this.downloadCsvLoading = true;
      let data = {
        query: `query {
          DevicesForOrderCsv (order_id: ${parseInt(this.$route.params.id)})
        }`
      };

      this.simpleFetch({data, this_ref: this}).then((res) => {
        if (res) {
          this.startDownloadCSV(res.DevicesForOrderCsv);
        }
      }).finally(()=>{
        this.downloadCsvLoading = false;
      });
    },
    startDownloadCSV(csv) {
      let d = new Date();
      let d_string = `${d.getUTCFullYear()}-`;
      d_string += `${("0" + (d.getUTCMonth() + 1)).slice(-2)}-`;
      d_string += `${("0" + d.getUTCDate()).slice(-2)}`;

      let link = document.getElementById("csvdownloadlink");
      link.href = "data:Application/octet-stream," + encodeURIComponent(csv);
      link.style.display = "block";
      link.download = `Order_id_${this.$route.params.id}_devices_${d_string}.csv`;
      link.click();
    },
    devicesAction(action) {
      const device_list = this.devices.filter(e => e.selected).map(a => a.imei);
      let actionMutation;

      switch (action) {
        case "stock":
          actionMutation = "DevicesStock";
          break;
        case "terminate":
          actionMutation = "DevicesTerminate";
          break;
        case "warranty":
          actionMutation = "DevicesWarranty";
          break;
      }

      let data = {
        query: `
          mutation ${actionMutation} (
            $device_list: [String],
            $order_id: ID
          ) { 
            res: ${actionMutation} (
              device_list: $device_list,
              order_id: $order_id
            )
          }
        `,
        variables: {
          device_list: device_list,
          order_id: this.$route.params.id
        }
      };
      const confirm_text = device_list.length >= 3 ? 'are you sure?' : null;
      this.simpleMutation({ data, this_ref: this, confirm_text }).then(() => {
        this.refreshOrder();
      });
    },
    undoTermination(id) {
      let data = {
        query: `
          mutation DeviceUndoTerminationByAgent (
            $id: ID!
          ) { 
            res: DeviceUndoTerminationByAgent (
              id: $id
            )
          }
        `,
        variables: {
          id: id
        }
      };
      const confirm_text = this.$t("globals.confirm_undo_termination");
      this.simpleMutation({ data, this_ref: this, confirm_text }).then(() => {
        this.refreshOrder();
      });
    },

  },
  computed: {
    ...mapGetters(["getUserBrandPermissions"]),
    devicesAny() {
      return (this.devices != null && this.devices.length > 0)
    },
    selectedItemsAny() {
      return (
        this.devicesAny &&
        this.devices.filter(e => e.selected).length > 0
      )
    },
    noSelectedDevices() {
      return (
        this.devicesAny
          ? this.devices.filter(e => e.selected).length
          : 0
      )
    },
    hasPendingTerminationDevice(){
      return this.devicesAny && this.devices.some(d => d.selected && d.pending_termination);
    },
    devicesLength() {
      return this.devicesAny ? this.devices.length : 0
    },
    filteredDevices() {
      if (!this.devices || this.devices.length === 0) return [];

      let filtered = this.devices;

      // Apply text filter
      if (this.filter && this.filter.trim() !== '') {
        const filterLower = this.filter.toLowerCase();
        filtered = filtered.filter(device => {
          return (
            (device.id && device.id.toString().includes(filterLower)) ||
            (device.imei && device.imei.toLowerCase().includes(filterLower)) ||
            (device.product && device.product.toLowerCase().includes(filterLower)) ||
            (device.batch && device.batch.toLowerCase().includes(filterLower)) ||
            (device.status && device.status.toLowerCase().includes(filterLower)) ||
            (device.return_process && device.return_process.toLowerCase().includes(filterLower))
          );
        });
      }

      return filtered;
    },
    sortedDevices() {
      if (!this.filteredDevices || this.filteredDevices.length === 0) return [];

      const sorted = [...this.filteredDevices].sort((a, b) => {
        const field = this.sortBy;
        let aVal = a[field];
        let bVal = b[field];

        if (aVal === null || aVal === undefined) return 1;
        if (bVal === null || bVal === undefined) return -1;

        if (typeof aVal === 'string') {
          aVal = aVal.toLowerCase();
          bVal = bVal.toLowerCase();
        }

        if (aVal < bVal) return this.sortDesc ? 1 : -1;
        if (aVal > bVal) return this.sortDesc ? -1 : 1;
        return 0;
      });

      return sorted;
    },
    paginatedDevices() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.sortedDevices.slice(start, end);
    },
    totalPages() {
      return Math.ceil((this.filteredDevices?.length || 0) / this.perPage);
    }
  },
  watch: {
    filter() {
      this.currentPage = 1;
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  }
};
</script>

<style scoped>
.devices-tab {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* Header Actions */
.header-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

.header-actions > * {
  flex-shrink: 0;
}

.primary-action-btn.disabled,
.primary-action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background-color: #cbd5e0;
}

.primary-action-btn.disabled:hover,
.primary-action-btn:disabled:hover {
  background-color: #cbd5e0;
}

/* Dropdown Menu */
.dropdown-wrapper {
  position: relative;
  display: inline-flex;
  flex-direction: column;
  align-items: flex-end;
}

.dropdown-menu {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  min-width: 150px;
  width: max-content;
  z-index: 10000;
  display: block;
}

.dropdown-item {
  display: block;
  width: 100%;
  padding: 10px 16px;
  text-align: left;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 0.875rem;
  color: #2d3748;
  transition: background-color 0.15s;
}

.dropdown-item:hover {
  background-color: #f7fafc;
}

.dropdown-item.danger {
  color: #dc3545;
}

.dropdown-item.danger:hover {
  background-color: #ffebee;
}

.dropdown-item:first-child {
  border-radius: 6px 6px 0 0;
}

.dropdown-item:last-child {
  border-radius: 0 0 6px 6px;
}

/* Filters Section */
.filters-section {
  display: flex;
  gap: 16px;
  align-items: end;
  margin-bottom: 16px;
}

.search-container {
  flex: 1;
  display: flex;
  gap: 8px;
}

.clear-btn {
  background-color: #ffc107;
  border: 1px solid #e0a800;
  color: #856404;
  padding: 0 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.2s;
  white-space: nowrap;
}

.clear-btn:hover {
  background-color: #e0a800;
  color: white;
}

/* Selected Info */
.selected-info {
  padding: 12px 16px;
  background-color: #f0f4ff;
  border-left: 4px solid #5469d4;
  border-radius: 4px;
  font-size: 0.875rem;
  color: #2d3748;
  margin-bottom: 16px;
}

/* Table Checkboxes */
.table-checkbox {
  width: 16px;
  height: 16px;
  cursor: pointer;
}

/* Terminate Cell */
.terminate-cell {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .header-actions {
    width: 100%;
  }

  .header-actions button {
    flex: 1;
    min-width: 120px;
  }

  .filters-section {
    flex-direction: column;
    align-items: stretch;
  }

  .filter-item {
    width: 100%;
  }
}
</style>
